import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import mermaid from 'mermaid'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

mermaid.initialize({
  startOnLoad: false,
  theme: 'neutral',
  fontFamily: 'inherit',
  securityLevel: 'antiscript',
  suppressErrorRendering: true,
})

interface MermaidBlockProps {
  code: string
  /** When true (message still streaming) skip the expensive render entirely.
   *  The diagram is rendered once the full code block is available. */
  isStreaming?: boolean
}

/** Résultat d'un rendu Mermaid, rattaché au code source qui l'a produit. */
type RenderResult =
  | { code: string; svg: string; error?: undefined }
  | { code: string; error: string; svg?: undefined }

export function MermaidBlock({ code, isStreaming = false }: MermaidBlockProps) {
  const { t } = useTranslation()
  const stableId = useId().replace(/[^a-zA-Z0-9]/g, '-')
  const [result, setResult] = useState<RenderResult | null>(null)
  const [zoomed, setZoomed] = useState(false)

  // Le résultat porte le code dont il provient : un rendu périmé est ignoré à
  // l'affichage, sans qu'il faille vider l'état depuis l'effet.
  const current = result?.code === code ? result : null

  useEffect(() => {
    // Pendant le streaming, le bloc de code est incomplet : le parser produirait
    // des erreurs et des mises en page D3 avortées.
    if (isStreaming) return

    let cancelled = false

    const renderDiagram = async () => {
      // Use a truly unique ID per render attempt - never reuse IDs with Mermaid
      const renderId = `mmd-${stableId}-${Date.now().toString(36)}`

      // Mermaid v11 needs a real DOM container attached to the document
      // Give it a realistic viewport width so it measures text correctly
      const host = document.createElement('div')
      host.style.cssText =
        'position:absolute;top:-9999px;left:-9999px;visibility:hidden;width:1200px;'
      document.body.appendChild(host)

      try {
        // Wait for web fonts to finish loading before Mermaid measures text.
        // Without this, Mermaid sizes nodes using fallback-font metrics but renders
        // with the actual (wider) web font, causing text to overflow the node boxes.
        await document.fonts.ready

        await mermaid.parse(code.trim())
        const { svg: rendered } = await mermaid.render(renderId, code.trim(), host)

        // Mermaid v11 outputs width="100%" on SVGs. Combined with a viewBox, this
        // causes the SVG to scale down to the container width. The foreignObject
        // content (node labels) uses CSS px — it does NOT scale with the SVG viewport,
        // so the clipPath rects become too small → text is visually clipped.
        // Fix: replace width="100%" with the natural pixel width from max-width style.
        const naturalWidth = rendered.match(/max-width:\s*([\d.]+)px/)?.[1]
        const fixedSvg = naturalWidth
          ? rendered.replace(
            /(<svg\b[^>]*?)\swidth="100%"/,
            `$1 width="${Math.ceil(Number(naturalWidth))}"`
          )
          : rendered
        if (!cancelled) setResult({ code, svg: fixedSvg })
      } catch (err: unknown) {
        if (!cancelled) {
          const raw = err instanceof Error ? err.message : String(err)
          const firstLine = raw.split('\n')[0] ?? raw
          setResult({ code, error: firstLine.slice(0, 120) })
        }
      } finally {
        host.remove()
        // Remove any stale error elements Mermaid may have injected
        document.querySelectorAll(`#${renderId}, [id^="dmermaid"], .mermaid-error`).forEach((el) => el.remove())
      }
    }

    void renderDiagram()

    return () => {
      cancelled = true
    }
  }, [code, stableId, isStreaming])

  if (current?.error) {
    return (
      <div className="my-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600 font-mono">
        {t('session.diagram_invalid', { reason: current.error })}
      </div>
    )
  }

  // During streaming, render nothing — the diagram will appear once the full
  // code block has been received and the message is finalized.
  if (isStreaming) return null

  if (!current?.svg) {
    return (
      <div className="my-2 h-20 rounded-lg bg-gray-100 animate-pulse" />
    )
  }

  return (
    <div className="my-3 w-full rounded-xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 bg-gray-50">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          {t('session.diagram')}
        </span>
        <button
          onClick={() => setZoomed((z) => !z)}
          className="text-[10px] text-[var(--color-primary)] hover:underline"
          type="button"
        >
          {zoomed ? t('session.diagram_shrink') : t('session.diagram_zoom')}
        </button>
      </div>

      {zoomed ? (
        <>
          <div className="h-[420px]">
            <TransformWrapper minScale={0.5} maxScale={4} doubleClick={{ mode: 'reset' }}>
              <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
                <div className="p-4 mermaid-svg-wrapper" dangerouslySetInnerHTML={{ __html: current.svg }} />
              </TransformComponent>
            </TransformWrapper>
          </div>
          <p className="text-center text-[10px] text-gray-400 py-1">
            {t('session.diagram_zoom_hint')}
          </p>
        </>
      ) : (
        // Non-zoomed: scroll container + inner div expands to natural SVG width
        <div className="overflow-x-auto">
          <div className="p-3 mermaid-svg-preview" dangerouslySetInnerHTML={{ __html: current.svg }} />
        </div>
      )}
    </div>
  )
}
